import { AnimatePresence, motion } from "framer-motion";
import { Shield, X, Settings, CheckCircle } from "lucide-react";

const PermissionsModal = ({ isOpen, onClose, teamMember, userPermissions }) => {
  if (!isOpen || !teamMember) return null;

  const permissions = userPermissions || {};

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="flex fixed top-0 right-0 bottom-0 left-0 z-50 justify-center items-center backdrop-blur-md bg-black/60"
        style={{ margin: 0, padding: 0 }}
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 50 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 50 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="relative w-full max-w-3xl max-h-[85vh] mx-4 my-4 overflow-hidden bg-white rounded-3xl shadow-2xl flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex-shrink-0 p-6 text-white bg-gradient-to-r from-purple-600 to-blue-600">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-full transition-all duration-200 text-white/80 hover:text-white hover:bg-white/20"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex gap-4 items-center">
              <div className="p-3 rounded-xl bg-white/20">
                <Shield className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{teamMember.full_name || teamMember.fullName}</h2>
                <p className="text-white/80">Role: {teamMember.role || 'No role assigned'}</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="overflow-y-auto flex-1 p-6">
            <h3 className="mb-6 text-lg font-semibold text-gray-800">Permissions & Access Rights</h3>
            
            {Object.keys(permissions).length > 0 ? (
              <div className="space-y-6">
                {Object.entries(permissions).map(([module, modulePermissions], index) => (
                  <motion.div
                    key={module}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="p-6 bg-gray-50 rounded-2xl border border-gray-200"
                  >
                    <h4 className="flex gap-2 items-center mb-4 font-semibold text-gray-800">
                      <Settings className="w-5 h-5 text-[#FF6C6C]" />
                      {module.replace(/_/g, ' ')}
                    </h4>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {(Array.isArray(modulePermissions) ? modulePermissions : []).map((permission, permIndex) => (
                        <motion.div
                          key={permission}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: (index * 0.1) + (permIndex * 0.05) }}
                          className="flex gap-2 items-center p-3 bg-white rounded-lg border border-gray-200"
                        >
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span className="text-sm text-gray-700">{permission.replace(/_/g, ' ')}</span>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col justify-center items-center py-12 text-center">
                <div className="p-4 mb-4 bg-gray-100 rounded-full">
                  <Shield className="w-8 h-8 text-gray-400" />
                </div>
                <h4 className="mb-2 text-lg font-semibold text-gray-800">No Permissions Assigned</h4>
                <p className="max-w-md text-gray-500">
                  This team member doesn't have any specific permissions assigned yet. 
                  Contact an administrator to assign appropriate permissions.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 p-6 bg-gray-50 border-t border-gray-200">
            <div className="flex justify-end">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onClose}
                className="px-6 py-3 font-medium text-gray-600 bg-white rounded-xl border-2 border-gray-200 transition-all duration-200 hover:bg-gray-50 hover:border-gray-300"
              >
                Close
              </motion.button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
export default PermissionsModal;